(ns io.daskul.webapp.widgets.display-logic
  "Pure value→text logic for the `:display` renderers in `display.cljs`.

   Separate namespace so it runs under the :node-test build — `display.cljs`
   pulls in the icon set, which does not resolve under node. Same split as
   `lookup_logic.cljs`."
  (:require [clojure.string :as str]))

(defn count-breakdown-segments
  "Ordered text segments for a per-state count map, e.g. {:active 3 :draft 1}.

   The config names the states, so nothing here knows which aggregate it is
   counting — a third state is a config change, not a change to this fn:

     :primary     - the state whose count IS the cell
     :secondary   - states appended as `· N <state>` when non-zero, in
                    DECLARED order. Keep it to 2: three fit a table cell,
                    four wrap it. Not enforced — a column that wants a
                    third should look wrong in review, not throw at runtime.
     :zero-label  - replaces the primary count when it is 0, in warning
                    treatment. Optional: without it a zero renders as \"0\",
                    because not every count of zero is a problem.

   A state present in `counts` but named by neither is counted and not shown.
   That is how archived packages stay out of the cell without the query
   having to know about them.

   Returns a vec of {:text :tone}; the renderer styles by :tone. Secondaries
   carry their own separator so the renderer can join on a plain space and
   still dim the `·`."
  [counts {:keys [primary secondary zero-label]}]
  (let [n (get counts (keyword primary) 0)]
    (into [(if (and (zero? n) zero-label)
             {:text zero-label :tone :warning}
             {:text (str n) :tone :primary})]
          (keep (fn [state]
                  (let [sn (get counts (keyword state) 0)]
                    (when (pos? sn)
                      {:text (str "· " sn " " (name state)) :tone :secondary}))))
          secondary)))

(defn count-breakdown-text
  "The segments as one line — what the cell reads."
  [counts config]
  (str/join " " (map :text (count-breakdown-segments counts config))))

(defn zero-amount?
  "True for a currency value that is nothing-owed — nil, or within a rounding
   hair of zero (balances are float subtractions). A real negative balance
   (overpayment / a credit) is not zero and still renders, so a credit is never
   hidden behind a dash."
  [v]
  (or (nil? v) (and (number? v) (< -0.005 v 0.005))))

(defn indicator
  "Resolve an `:indicator` cell (#858/#870) to what renders — kept pure so the
   label, tooltip, colour key and the dash decision are unit-tested without a
   browser. The widget in `display.cljs` only draws the result.

   One emptiness rule: a nil value renders nothing (→ the cell's dash). A real 0
   is a value and renders as \"0\" — hiding a count at zero is the data
   producer's job (the Unbilled column already arrives nil at zero), not a knob
   here.

   Two shapes on the one renderer:
     - enum  (Status): `:values` maps the value → {:label :tone}, injected from
       the per-row state-UI by the caller, so colour + label share the badge's
       one source. No tooltip — the label is already spelled out.
     - scalar (Unbilled): no `:values`; the label is the number, the tone is the
       config's `:tone`, and `:singular` spells the tooltip out (pluralised by a
       trailing \"s\" unless N is 1 — the rule the server-side alert uses too).

   `:color-source` is the UI-metadata map the caller feeds to `colors/badge-colors`
   — the matched state-UI for an enum (so its `:variant` fallback still resolves),
   or `{:tone …}` for a scalar. Colour stays resolved in one place, not here.

   Returns nil (→ dash) or {:label :color-source :description}."
  [value {:keys [values singular tone]}]
  (when (some? value)
    (if-let [vc (or (get values (keyword value)) (get values value))]
      {:label (:label vc) :color-source vc}
      {:label (str value)
       :color-source {:tone tone}
       :description (when singular
                      (str value " " singular (when (not= 1 value) "s")))})))

(defn state-cell-display-config
  "The `:display` config for a state value in a DENSE list / child-table cell
   (#858). Defaults to the light `:indicator` dot; an explicit column `:display`
   overrides the weight. Either way the per-row state-UI is injected as
   `:values`, so colour + label resolve through the badge's one source and a
   dot never disagrees with the pill.

   This is the rollout default: every aggregate's Status column reads as the
   lighter dot without opting in per-column. The focal detail header does NOT
   use this — it keeps the full badge (`display/state-ui->display-config`).
   Table-light, detail-strong is itself the consistent convention.

   `state` is the raw value string; the caller keys `:values` by it so the
   string/keyword round-trip is handled downstream by `indicator`."
  [state state-ui explicit-display]
  (assoc (or explicit-display {:type :indicator :icon "dot"})
         :values {state state-ui}))
